import { useTranslation } from 'react-i18next'
import { usePageContext } from 'pageContext'
import { isNilOrEmpty, useAuthState } from 'packages/core'
import DateTimeFilter from './DateTimeFilter'
import PersonFilter from './PersonFilter'
import { Card } from 'packages/eid-ui'
import AdvancedFilter from './EidFilter/AdvancedFilter'
import { Icon } from 'packages/eid-icons'
import { Box, Typography } from '@mui/material'
import ApplicationsFilter from './ApplicationsFilter'
import CSVInboxCategoryFilter from './CSVInboxCategory/CSVInboxCategory'
import { TextSearchFilter } from 'packages/eid-ui'
import React, { useEffect } from 'react'
import InboxTypeFilter from './InboxType'
import WithoutApprovalFilter from './WithoutApprovalFilter'
import InboxItemStatusFilter from './InboxItemStatusFilter'
import GroupSelectionFilter from './GroupSelectionFilter'
import OrgRoleOrgZoneFilter from './OrgRoleOrgZoneFilter'
import OrgRoleFilter from './OrgRoleFilter'
import OrgZoneFilter from './OrgZoneFilter'
import AccountStoreFilter from './AccountStoreFilter'
import AssigneeFilter from './AssigneeFilter'
import useSubcomponents from 'useSubcomponents'
import DatePickerFilter from './EidFilter/GenericFilter/DatePickerFilter'
import TextualFilter from './EidFilter/GenericFilter/TextualFilter'

/**
 * Wrapper for the reusable TextSearchFilter component
 * This provides backward compatibility for the <Filters.TextSearch /> component
 * and handles pageContext integration.
 *
 * @param {Object} props - Component props
 * @param {boolean} [props.outlined=false] - Whether to use outlined styling (optional)
 * @param {string} [props.placeholder=''] - Placeholder text for the search input (optional)
 * @param {string} [props.variant='widthSm'] - Size variant (optional)
 * @param {number} [props.minCharLength=3] - Minimum characters required for search (optional)
 * @param {number} [props.debounceTime=500] - Debounce time in milliseconds (optional)
 * @param {boolean} [props.hideSearchInfoMessage=false] - Whether to hide the search info message (optional)
 * @param {string} [props.searchInfoMessage='Min_Search_Char_Limit'] - Custom translation key for the search info message (optional)
 * @param {function} [props.onEnterPress=null] - Callback function to handle Enter key press (optional)
 * @returns {React.Component} Wrapped TextSearchFilter with consistent styling
 */
const TextSearchFilterWrapper = React.forwardRef((props, ref) => {
    // Destructure props, excluding ones we want to control
    const {
        outlined = false,
        placeholder = '',
        variant = 'widthSm',
        minCharLength = 3,
        debounceTime = 500,
        hideSearchInfoMessage = false,
        onEnterPress = null,
        searchInfoMessage = 'Min_Search_Char_Limit',
        // Exclude onChange and value - we'll manage these
        onChange: _onChange,
        value: _value,
        ...restProps
    } = props

    const [state, dispatch] = usePageContext()

    // Handler that will update the pageContext
    const handleSearch = (searchValue) => {
        // Only update if empty (cleared) or with at least minCharLength chars
        if (searchValue?.length === 0 || searchValue?.length >= minCharLength) {
            dispatch({
                type: 'SET_PROP',
                payload: { key: 'searchTerm', value: searchValue },
            })
        }
    }

    // Explicitly return React element to make it work with TypeScript
    return React.createElement(TextSearchFilter, {
        // Allow overridable props
        variant,
        outlined,
        placeholder,
        minCharLength,
        debounceTime,
        hideSearchInfoMessage,
        onEnterPress,
        searchInfoMessage,
        ref,
        // These props cannot be overridden
        value: state.searchTerm || '',
        onChange: handleSearch,
        // Pass through all other props
        ...restProps,
    })
})

// Add display name for React DevTools and TypeScript
TextSearchFilterWrapper.displayName = 'TextSearchFilterWrapper'

const StartDateFilter = () => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()

    return (
        <DateTimeFilter
            disableFuture
            showLabelWithin
            placeholder={t('BetweenDate')}
            value={
                isNilOrEmpty(state.startDate)
                    ? null
                    : state.startDate.clone().local()
            }
            onChange={(date) => {
                const dateToSet = date.clone()
                dateToSet.startOf('day')
                dateToSet.utc()

                dispatch({
                    type: 'SET_PROP',
                    payload: {
                        key: 'startDate',
                        value: dateToSet,
                    },
                })
            }}
            handleClear={() =>
                dispatch({
                    type: 'SET_PROP',
                    payload: {
                        key: 'startDate',
                        value: null,
                    },
                })
            }
        />
    )
}

const EndDateFilter = () => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()

    return (
        <DateTimeFilter
            disableFuture
            showLabelWithin
            placeholder={t('AndDate')}
            value={
                isNilOrEmpty(state.endDate)
                    ? null
                    : state.endDate.clone().local()
            }
            onChange={(date) => {
                const dateToSet = date.clone().endOf('day')
                dateToSet.utc()

                dispatch({
                    type: 'SET_PROP',
                    payload: {
                        key: 'endDate',
                        value: dateToSet,
                    },
                })
            }}
            handleClear={() =>
                dispatch({
                    type: 'SET_PROP',
                    payload: {
                        key: 'endDate',
                        value: null,
                    },
                })
            }
        />
    )
}

const dateFilterStyles = {
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '15px',
    boxShadow: '0 4px 6px -2px rgba(0, 0, 0, 0.1)',
    border: 'solid 1px #ebebed',
    borderRadius: '5px',
    backgroundColor: '#ffffff',
    color: '#7D7C7C',
}

const DateFilter = ({ label }) => {
    return (
        <Box style={dateFilterStyles}>
            <Box minWidth="120px">{label}</Box>
            <StartDateFilter />
            <EndDateFilter />
        </Box>
    )
}

// Generic date filter — same pattern as StartDateFilter / EndDateFilter but
// driven by config: { label: string, contextProp: string, placeholder?: string }
const DatePicker = ({ config = {} }) => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()
    const { label = '', contextProp, placeholder } = config

    const currentValue = state[contextProp]

    return (
        <Card cardTitle={t(label)} collapsible expanded={Boolean(currentValue)}>
            <Box padding="20px 20px 0px 20px ">
                 <DatePickerFilter
                value={currentValue}
                onChange={(date) => {
                    dispatch({
                        type: 'SET_PROP',
                        payload: { key: contextProp, value: date },
                    })
                }}
                placeholder={t('SelectDate')}
                // label={t(label)}
            /></Box>

        </Card>
    )
}

// Generic text filter — driven by config: { label: string, contextProp: string, placeholder?: string }
const TextFilter = ({ config = {} }) => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()
    const { label = '', contextProp, placeholder } = config

    const currentValue = state[contextProp]

    return (
        <Card
            cardTitle={t(label)}
            collapsible
            expanded={Boolean(currentValue)}
        >
            <Box padding="20px 20px 0px 20px">
                <TextualFilter
                    value={currentValue || ''}
                    onChange={(value) =>
                        dispatch({
                            type: 'SET_PROP',
                            payload: { key: contextProp, value: value || null },
                        })
                    }
                    placeholder={t(placeholder || label)}
                />
            </Box>
        </Card>
    )
}

const OwnedByFilter = ({ config }) => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()
    const [{ currentUserId, friendlyName }] = useAuthState()
    const { hasAccessToControl } = useSubcomponents()
    const hasAccessToOwnedByFilter = config.protectdSubcomponent
        ? hasAccessToControl(config.protectdSubcomponent)
        : true

    useEffect(() => {
        if (!hasAccessToOwnedByFilter) {
            dispatch({
                type: 'SET_MULTIPLE_PROPS',
                payload: {
                    ownedBy: {
                        id: currentUserId,
                        friendlyName: friendlyName,
                    },
                },
            })
        }
    }, [currentUserId])

    return (
        <Card
            cardTitle={t('UploadedBy')}
            collapsible
            expanded={state.ownedBy !== null}
        >
            {hasAccessToOwnedByFilter ? (
                <PersonFilter
                    value={state.ownedBy}
                    onClear={() =>
                        dispatch({
                            type: 'SET_PROP',
                            payload: {
                                key: 'ownedBy',
                                value: {
                                    id: currentUserId,
                                    friendlyName: friendlyName,
                                },
                            },
                        })
                    }
                    onChange={(payload) => {
                        dispatch({
                            type: 'SET_PROP',
                            payload: {
                                key: 'ownedBy',
                                value: payload,
                            },
                        })
                    }}
                />
            ) : (
                <Box sx={{ padding: '20px' }}>
                    <Typography>{t('Myself')}</Typography>
                </Box>
            )}
        </Card>
    )
}

const AdvancedSearchFilters = ({ config }) => {
    const { t } = useTranslation()
    const [state] = usePageContext()

    return (
        <Card
            cardTitle={t('Common_AdvancedSearch')}
            collapsible
            icon={<Icon name="AdvancedSearch" color="#959598" />}
            expanded={
                config.filters.some((x) => state[x.contextProp]) ||
                state[config.tags.contextProp]
            }
        >
            <Box padding="20px 20px 0px 20px ">
                <AdvancedFilter forms={config} tags={config.tags} />
            </Box>
        </Card>
    )
}

const Filters = () => <></>

Filters.TextSearch = TextSearchFilterWrapper
Filters.StartDate = StartDateFilter
Filters.EndDate = EndDateFilter
Filters.DateFilter = DateFilter
Filters.OwnedByFilter = OwnedByFilter
Filters.WithoutApprovalFilter = WithoutApprovalFilter
Filters.ApplicationsFilter = ApplicationsFilter
Filters.CSVInboxCategoryFilter = CSVInboxCategoryFilter
Filters.AdvancedSearchFilters = AdvancedSearchFilters
Filters.InboxTypeFilter = InboxTypeFilter
Filters.InboxItemStatusFilter = InboxItemStatusFilter
Filters.GroupSelectionFilter = GroupSelectionFilter
Filters.OrgRoleOrgZoneFilter = OrgRoleOrgZoneFilter
Filters.OrgRoleFilter = OrgRoleFilter
Filters.OrgZoneFilter = OrgZoneFilter
Filters.AccountStoreFilter = AccountStoreFilter
Filters.AssigneeFilter = AssigneeFilter
Filters.DatePickerFilter = DatePicker
Filters.TextFilter = TextFilter

const filtersMap = {
    OwnedByFilter,
    withoutApproval: WithoutApprovalFilter,
    applicationsFilter: ApplicationsFilter,
    advancedSearch: AdvancedSearchFilters,
    inboxCategoryFilter: CSVInboxCategoryFilter,
    inboxTypeFilter: InboxTypeFilter,
    inboxItemStatusFilter: InboxItemStatusFilter,
    groupSelectionFilter: GroupSelectionFilter,
    orgRoleOrgZoneFilter: OrgRoleOrgZoneFilter,
    orgRoleFilter: OrgRoleFilter,
    orgZoneFilter: OrgZoneFilter,
    accountStoreFilter: AccountStoreFilter,
    assigneeFilter: AssigneeFilter,
    uploadedAfterFilter: DatePicker,
    uploadedBeforeFilter: DatePicker,
    textFilter: TextFilter,
}

Filters.filtersMap = filtersMap

export default Filters
